/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { ICartSpecificProductContext, TContextProvider } from '../../../interfaces';
import { ProactiveChatNames } from '../../helpers';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * CartSpecificProductTrigger class.
 * Proactive chat is triggered if a Product in the Cart is in preset list of products (config.proactiveOnCartSpecificProductList).
 */
export default class CartSpecificProductTrigger extends ProactiveChatTrigger {
    private product: string = '';

    /**
     * Validates whether the condition(s) is met to trigger proactive chat: the cart contains the specified product to trigger proactice chat.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        let isValid = false;
        const cart = await this._getCart();
        if (!cart.isEmpty) {
            if (cart.cart.CartLines) {
                this.props.context.telemetry.debug(
                    `[${CartSpecificProductTrigger.name}] Number of cart line items was '${cart.cart.CartLines?.length}'`
                );

                for (let i = 0; i < cart.cart.CartLines?.length; i++) {
                    if (cart.cart.CartLines[i]) {
                        if (this.props.config.proactiveOnCartSpecificProductList) {
                            this.props.context.telemetry.debug(
                                `[${CartSpecificProductTrigger.name}] Number of cart specific products was configured for proactive chat was '${this.props.config.proactiveOnCartSpecificProductList?.length}'`
                            );

                            // Find first item, from user-defined list of products, that matches the cart lines' itemId in the cart.
                            for (let j = 0; j < (this.props.config.proactiveOnCartSpecificProductList?.length || 0); j++) {
                                if (
                                    this.props.config.proactiveOnCartSpecificProductList &&
                                    cart.cart.CartLines[i].ItemId &&
                                    this.props.config.proactiveOnCartSpecificProductList[j] === cart.cart.CartLines[i].ItemId
                                ) {
                                    this.product = cart.cart.CartLines[i].ItemId || '';
                                    this.props.context.telemetry.debug(
                                        `[${CartSpecificProductTrigger.name}] Matched cart line item ID '${cart.cart.CartLines[i].ItemId}' against user-defined list of specific products for proactive chat.`
                                    );
                                    isValid = true;
                                    break;
                                }
                            }
                        }
                    } else {
                        // Rest of the CartLines are undefined
                        break;
                    }
                }
            } else {
                this.props.context.telemetry.debug(`[Chat][${CartSpecificProductTrigger.name}] Cart did not contain any cart lines.`);
            }
        } else {
            this.props.context.telemetry.debug(`[Chat][${CartSpecificProductTrigger.name}] Cart was empty.`);
        }

        this.props.context.telemetry.debug(`[Chat][${CartSpecificProductTrigger.name}] Validation returning '${isValid}'.`);
        return isValid;
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): ICartSpecificProductContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.SpecificProduct,
                    isDisplayable: true
                },
                CartSpecificProduct: {
                    value: this.product,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnCartSpecificProductMessage || this.props.config.proactiveDefaultMessage;
    }

    /**
     * Gets the cart.
     * @returns {Promise<ICartState>} The cart.
     */
    private async _getCart(): Promise<ICartState> {
        return getCartState(this.props.context.actionContext);
    }
}
